import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const PROVIDERS = [
    { id: 'google', name: 'Google Calendar' },
    { id: 'microsoft_outlook365', name: 'Microsoft Outlook 365' }
];

function CalendarConnectionsView({ theme = 'light' }) {
    const [state, setState] = useState({ loading: true, driver: null, providers: {}, connections: [], error: null });
    const [rateCard, setRateCard] = useState({ basis: 'per_trip', value: '0.00', saving: false, message: '' });

    const request = useCallback(async (path, options = {}) => {
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (!session?.access_token) throw new Error('Sign in again to manage calendar connections.');

        const authHeader = ['Bearer', session.access_token].join(' ');
        const response = await fetch(path, {
            ...options,
            headers: {
                ...(options.headers || {}),
                Authorization: authHeader
            }
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Calendar request failed.');
        return body;
    }, []);

    const loadConnections = useCallback(async () => {
        try {
            setState((current) => ({ ...current, loading: true, error: null }));
            const body = await request('/api/calendar/connections');
            setState({ ...body, loading: false, error: null });
        } catch (error) {
            setState((current) => ({ ...current, loading: false, error: error.message }));
        }
    }, [request]);

    useEffect(() => {
        loadConnections();
    }, [loadConnections]);

    useEffect(() => {
        if (!state.driver) return;
        setRateCard((current) => ({
            ...current,
            basis: state.driver.payroll_rate_card_basis || 'per_trip',
            value: String(state.driver.payroll_rate_card_value ?? '0.00'),
            message: ''
        }));
    }, [state.driver]);

    const connect = async (provider) => {
        try {
            const body = await request(`/api/calendar/connect?provider=${provider}`);
            window.location.assign(body.authorizationUrl);
        } catch (error) {
            setState((current) => ({ ...current, error: error.message }));
        }
    };

    const disconnect = async (provider) => {
        try {
            await request(`/api/calendar/connections?provider=${provider}`, { method: 'DELETE' });
            await loadConnections();
        } catch (error) {
            setState((current) => ({ ...current, error: error.message }));
        }
    };

    const saveRateCard = async () => {
        try {
            setRateCard((current) => ({ ...current, saving: true, message: '' }));
            await request('/api/calendar/connections', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payroll_rate_card_basis: rateCard.basis,
                    payroll_rate_card_value: rateCard.value
                })
            });
            setRateCard((current) => ({ ...current, saving: false, message: 'Rate card saved.' }));
            await loadConnections();
        } catch (error) {
            setRateCard((current) => ({ ...current, saving: false, message: error.message || 'Unable to save rate card.' }));
        }
    };

    const connectionFor = (provider) => state.connections.find((connection) => connection.provider === provider);
    const panelClass = theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900';

    return (
        <div className="max-w-3xl mx-auto">
            <div className={`rounded-lg border p-6 ${panelClass}`}>
                <h2 className="text-2xl font-bold">My Calendar Connections</h2>
                <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Connect your own calendar so Dispatch can avoid known busy times. IntelAgent reads busy/free windows only, never event titles, attendees, or descriptions.
                </p>
                {state.loading && <p className="mt-6 text-sm">Loading calendar connections…</p>}
                {state.error && <p className="mt-6 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{state.error}</p>}
                {!state.loading && !state.driver && (
                    <p className="mt-6 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                        This signed-in account is not linked to a driver profile, so it cannot connect a driver calendar.
                    </p>
                )}
                {!state.loading && state.driver && (
                    <div className="mt-6 space-y-4">
                        <div className={`rounded border p-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                            <h3 className="font-semibold">My 1099 Rate Card</h3>
                            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                This profile field drives payroll set-aside split calculations (per trip or per mile).
                            </p>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="text-sm">
                                    <span className="block mb-1">Basis</span>
                                    <select
                                        value={rateCard.basis}
                                        onChange={(event) => setRateCard((current) => ({ ...current, basis: event.target.value }))}
                                        className={`w-full rounded border px-3 py-2 ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}`}
                                    >
                                        <option value="per_trip">Per Trip</option>
                                        <option value="per_mile">Per Mile</option>
                                    </select>
                                </label>
                                <label className="text-sm">
                                    <span className="block mb-1">Rate ($)</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={rateCard.value}
                                        onChange={(event) => setRateCard((current) => ({ ...current, value: event.target.value }))}
                                        className={`w-full rounded border px-3 py-2 ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}`}
                                    />
                                </label>
                            </div>
                            <div className="mt-3 flex items-center gap-3">
                                <button
                                    onClick={saveRateCard}
                                    disabled={rateCard.saving}
                                    className="rounded bg-orange-600 px-3 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                                >
                                    {rateCard.saving ? 'Saving…' : 'Save Rate Card'}
                                </button>
                                {rateCard.message && (
                                    <span className={`text-sm ${rateCard.message === 'Rate card saved.' ? 'text-green-600' : 'text-red-600'}`}>
                                        {rateCard.message}
                                    </span>
                                )}
                            </div>
                        </div>

                        {PROVIDERS.map((provider) => {
                            const connection = connectionFor(provider.id);
                            const enabled = state.providers[provider.id];
                            return (
                                <div key={provider.id} className={`flex items-center justify-between gap-4 rounded border p-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div>
                                        <h3 className="font-semibold">{provider.name}</h3>
                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {!enabled ? 'Disabled by your organization.' : connection ? `Status: ${connection.sync_status}` : 'Not connected'}
                                        </p>
                                    </div>
                                    {connection && connection.sync_status !== 'disconnected' ? (
                                        <button onClick={() => disconnect(provider.id)} className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700">
                                            Disconnect
                                        </button>
                                    ) : (
                                        <button disabled={!enabled} onClick={() => connect(provider.id)} className="rounded bg-orange-600 px-3 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
                                            Connect
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default CalendarConnectionsView;
